import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import AtlasBlocksSection from "@/components/AtlasBlocksSection";
import BackButton from "@/components/BackButton";
import {
  ArrowLeft, Globe, MapPin, Building2, Star, Phone, Mail, MapPinned, Trees,
  Mountain, Utensils, Hotel, ShoppingBag, ScrollText, Clock, ExternalLink,
  ShieldCheck, X, Loader2, BadgeCheck,
} from "lucide-react";

const POI_ICON = { cellar: Building2, vineyard: Trees, view: Mountain, restaurant: Utensils, lodging: Hotel, shop: ShoppingBag, history: ScrollText };

function Rating({ value, count }) {
  if (!value) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm" data-testid="producer-rating">
      <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
      <span className="font-bold text-[#1F3A5F]">{value.toFixed(1)}</span>
      {count != null && <span className="text-[#8B95A1]">({count.toLocaleString("fr-FR")} avis)</span>}
    </span>
  );
}

function OpenStatus({ openNow }) {
  if (openNow == null) return null;
  return openNow ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[11px] font-bold" data-testid="producer-open-status">
      <Clock className="w-3 h-3" /> Ouvert
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-[11px] font-bold" data-testid="producer-open-status">
      <Clock className="w-3 h-3" /> Fermé
    </span>
  );
}

function ClaimModal({ producer, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!user) { navigate(`/login?next=/producers/${producer.slug}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/producers/claim", {
        place_id: producer.google_place_id || producer.places?.place_id,
        name: producer.name,
        address: producer.address || producer.places?.address,
        website: producer.website || producer.places?.website,
        phone: producer.phone || producer.places?.phone,
        region: producer.region || producer.places?.region,
        country: producer.country || producer.places?.country,
        note,
      });
      toast.success(data?.message || "Demande envoyée");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#1F3A5F]/60 flex items-end md:items-center justify-center md:p-5 overlay-safe" data-testid="claim-modal">
      <div className="bg-white w-full max-w-lg shadow-2xl flex flex-col rounded-t-3xl md:rounded-3xl max-h-[calc(100dvh_-_var(--safe-top)_-_0.5rem)] md:max-h-[85dvh] pb-safe md:pb-0">
        <div className="overflow-y-auto p-6 md:p-7">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FCE4ED] flex items-center justify-center shrink-0">
              <ShieldCheck className="w-5 h-5 text-[#E5276A]" />
            </div>
            <div>
              <h3 className="font-display text-2xl text-[#1F3A5F]">Revendiquer ce domaine</h3>
              <p className="text-sm text-[#4A5568] mt-1">{producer.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-[#8B95A1] hover:text-[#1F3A5F]" data-testid="claim-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-[#4A5568] mt-4">
          Vous êtes le propriétaire, gérant ou représentant officiel ? Nous validons votre demande sous 48 h.
          Vous pourrez ensuite éditer la fiche, ajouter visites, événements et publier des artefacts.
        </p>
        <label className="block mt-5">
          <span className="text-[11px] uppercase tracking-[0.18em] text-[#E5276A] font-semibold">Message (optionnel)</span>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Précisez votre fonction et un moyen de vérifier votre identité (site, email pro, etc.)"
            data-testid="claim-note"
            className="mt-1 w-full bg-white border border-[#1F3A5F]/15 focus:border-[#E5276A] focus:ring-2 focus:ring-[#E5276A]/10 outline-none rounded-lg px-4 py-3 text-sm text-[#1F3A5F]"
          />
        </label>
        <div className="mt-6 flex gap-3 justify-end">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 rounded-xl border border-[#1F3A5F]/15 text-sm font-bold text-[#1F3A5F]" data-testid="claim-cancel">
            Annuler
          </button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E5276A] text-white text-sm font-bold hover:bg-[#C01661] disabled:opacity-60" data-testid="claim-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            Envoyer la demande
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

export default function ProducerDetail() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  const [mapArtifacts, setMapArtifacts] = useState([]);
  const [showClaim, setShowClaim] = useState(false);

  useEffect(() => {
    setState("loading");
    api.get(`/producers/${slug}`)
      .then((r) => { setItem(r.data); setState("ok"); })
      .catch(() => { setItem(null); setState("error"); });
    api.get(`/pro/map-builder/public/${slug}`)
      .then((r) => setMapArtifacts(r.data?.artifacts || []))
      .catch(() => setMapArtifacts([]));
  }, [slug]);

  if (state === "loading") return <div className="max-w-4xl mx-auto px-6 py-24 text-[#8B95A1]">Chargement…</div>;
  if (state === "error" || !item) {
    return (
      <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="producer-detail-404">
        <BackButton fallback="/producers" />
        <div className="mt-8 rounded-2xl border border-dashed border-[#1F3A5F]/15 bg-[#FBF7F3] p-8 text-center">
          <h1 className="font-display text-3xl text-[#1F3A5F]">Fiche introuvable</h1>
          <p className="mt-2 text-sm text-[#4A5568]">
            Le domaine « {slug} » n'existe pas encore dans notre base. Il sera bientôt ajouté.
          </p>
          <Link
            to="/producers"
            className="mt-5 inline-flex items-center gap-1.5 text-sm text-[#E5276A] font-bold hover:underline"
          >
            Explorer les producteurs référencés →
          </Link>
        </div>
      </div>
    );
  }

  // Merge live places data with stored fiche (places takes precedence on dynamic fields)
  const places = item.places || {};
  const address = item.address || places.address;
  const website = item.website || places.website;
  const phone = item.phone || places.phone;
  const rating = places.rating ?? item.rating;
  const ratingCount = places.rating_count ?? item.rating_count;
  const gmapsUrl = places.gmaps_url;
  const openNow = places.open_now;
  const openingHours = places.opening_hours;
  const isClaimed = !!item.owner_id;

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="producer-detail">
      <BackButton fallback="/producers" />

      <div className="mt-6">
        <span className="chip-pink">{item.type} · {[item.region, item.country].filter(Boolean).join(", ")}</span>
        <div className="mt-4 flex items-start justify-between flex-wrap gap-3">
          <h1 className="font-display text-5xl md:text-6xl text-[#1F3A5F] leading-[1.05]">{item.name}</h1>
          {isClaimed ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FCE4ED] text-[#E5276A] text-xs font-bold" data-testid="producer-verified">
              <BadgeCheck className="w-4 h-4" /> Fiche vérifiée
            </span>
          ) : (
            <button
              onClick={() => setShowClaim(true)}
              data-testid="claim-cta"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Revendiquer ce domaine
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Rating value={rating} count={ratingCount} />
          <OpenStatus openNow={openNow} />
          {places.business_status && places.business_status !== "OPERATIONAL" && (
            <span className="text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full font-bold">
              {places.business_status === "CLOSED_TEMPORARILY" ? "Fermé temporairement" : places.business_status === "CLOSED_PERMANENTLY" ? "Définitivement fermé" : places.business_status}
            </span>
          )}
        </div>
      </div>

      {item.image_url && (
        <div className="mt-6 rounded-3xl overflow-hidden border border-[#1F3A5F]/8 bg-[#FBF7F3] aspect-[16/8]">
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
        </div>
      )}

      {(item.description || places.summary) && (
        <p className="mt-8 text-base md:text-lg text-[#1F3A5F] leading-relaxed font-light" data-testid="producer-description">
          {item.description || places.summary}
        </p>
      )}

      {/* Contact + practical info */}
      <div className="mt-10 grid md:grid-cols-2 gap-4" data-testid="producer-contact-grid">
        {website && (
          <a href={website} target="_blank" rel="noreferrer" className="app-card p-5 flex items-center gap-3 hover:border-[#E5276A] transition-colors" data-testid="producer-website">
            <div className="w-10 h-10 rounded-lg bg-[#FCE4ED] flex items-center justify-center shrink-0"><Globe className="w-5 h-5 text-[#E5276A]" /></div>
            <div className="min-w-0">
              <div className="text-xs text-[#8B95A1]">Site web</div>
              <div className="text-sm text-[#1F3A5F] truncate">{website.replace(/^https?:\/\/(www\.)?/, "")}</div>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-[#8B95A1] ml-auto shrink-0" />
          </a>
        )}
        {address && (
          <a
            href={gmapsUrl || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
            target="_blank" rel="noreferrer"
            className="app-card p-5 flex items-center gap-3 hover:border-[#E5276A] transition-colors"
            data-testid="producer-address"
          >
            <div className="w-10 h-10 rounded-lg bg-[#FCE4ED] flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-[#E5276A]" /></div>
            <div className="min-w-0">
              <div className="text-xs text-[#8B95A1]">Adresse</div>
              <div className="text-sm text-[#1F3A5F]">{address}</div>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-[#8B95A1] ml-auto shrink-0" />
          </a>
        )}
        {phone && (
          <a href={`tel:${phone.replace(/\s/g, "")}`} className="app-card p-5 flex items-center gap-3 hover:border-[#E5276A] transition-colors" data-testid="producer-phone">
            <div className="w-10 h-10 rounded-lg bg-[#FCE4ED] flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-[#E5276A]" /></div>
            <div className="min-w-0">
              <div className="text-xs text-[#8B95A1]">Téléphone</div>
              <div className="text-sm text-[#1F3A5F]">{phone}</div>
            </div>
          </a>
        )}
        {item.email && (
          <a href={`mailto:${item.email}`} className="app-card p-5 flex items-center gap-3 hover:border-[#E5276A] transition-colors" data-testid="producer-email">
            <div className="w-10 h-10 rounded-lg bg-[#FCE4ED] flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-[#E5276A]" /></div>
            <div className="text-sm text-[#1F3A5F]">{item.email}</div>
          </a>
        )}
      </div>

      {/* Opening hours */}
      {openingHours && openingHours.length > 0 && (
        <div className="mt-8 app-card p-6" data-testid="producer-hours">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-[#E5276A]" />
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold">Horaires d'ouverture</div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-y-1.5 gap-x-6 text-sm text-[#1F3A5F]">
            {openingHours.map((h, i) => (
              <li key={i} className="font-mono text-xs">{h}</li>
            ))}
          </ul>
          <p className="mt-3 text-xs text-[#8B95A1] italic">Source : Google Places — confirmez auprès du domaine pour les visites privées.</p>
        </div>
      )}

      {(item.appellations?.length > 0 || item.cepages?.length > 0) && (
        <div className="mt-10 grid md:grid-cols-2 gap-8">
          {item.appellations?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3">Appellations</div>
              <div className="flex flex-wrap gap-2">{(item.appellations || []).map((a) => (
                <span key={a} className="px-3 py-1.5 rounded-full border border-[#1F3A5F]/12 text-sm text-[#1F3A5F] bg-white">{a}</span>
              ))}</div>
            </div>
          )}
          {item.cepages?.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3">Cépages</div>
              <div className="flex flex-wrap gap-2">{(item.cepages || []).map((c) => (
                <span key={c} className="px-3 py-1.5 rounded-full border border-[#1F3A5F]/12 text-sm text-[#1F3A5F] bg-white">{c}</span>
              ))}</div>
            </div>
          )}
        </div>
      )}

      {item.story?.published && item.story?.sections && Object.keys(item.story.sections).length > 0 && (
        <div className="mt-16 space-y-12" data-testid="producer-story">
          {item.story.sections.hero?.body && (
            <p className="text-xl md:text-2xl text-[#1F3A5F] leading-relaxed font-light italic border-l-4 border-[#E5276A] pl-6">
              {item.story.sections.hero.body}
            </p>
          )}
          {["history", "terroir", "wines", "philosophy", "visit"].map((k) => {
            const s = item.story.sections[k];
            if (!s?.body) return null;
            return (
              <section key={k} data-testid={`producer-story-${k}`}>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3">{s.title}</div>
                <div className="text-base text-[#1F3A5F] leading-relaxed font-light whitespace-pre-wrap">{s.body}</div>
              </section>
            );
          })}
          {item.story.sections.anecdote?.body && (
            <section data-testid="producer-story-anecdote" className="rounded-2xl bg-[#FBF7F3] border border-[#1F3A5F]/10 p-6 md:p-8">
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3">{item.story.sections.anecdote.title}</div>
              <div className="text-base text-[#4A5568] leading-relaxed italic whitespace-pre-wrap">{item.story.sections.anecdote.body}</div>
            </section>
          )}
        </div>
      )}

      {mapArtifacts.length > 0 && (
        <div className="mt-16 space-y-10" data-testid="producer-map-artifacts">
          {mapArtifacts.filter((a) => a.type === "tourism_route").map((a) => (
            <section key={a.id} data-testid={`producer-route-${a.id}`}>
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-2 inline-flex items-center gap-1.5"><MapPinned className="w-3.5 h-3.5" /> {a.payload?.audience || a.audience || "Visite"}</div>
                  <h2 className="font-display text-2xl md:text-3xl text-[#1F3A5F]">{a.name}</h2>
                </div>
                {a.payload?.total_min && <span className="text-xs text-[#4A5568] font-bold">≈ {a.payload.total_min} min</span>}
              </div>
              {a.payload?.summary && <p className="mt-3 text-base text-[#1F3A5F] leading-relaxed italic">{a.payload.summary}</p>}
              <ol className="mt-5 space-y-3">
                {(a.payload?.pois || []).map((poi, idx) => {
                  const Icon = POI_ICON[poi.type] || MapPinned;
                  return (
                    <li key={idx} className="rounded-xl border border-[#1F3A5F]/10 bg-[#FBF7F3] p-4 flex gap-3">
                      <div className="flex flex-col items-center gap-1 shrink-0">
                        <span className="w-7 h-7 rounded-full bg-[#E5276A] text-white text-xs font-bold inline-flex items-center justify-center">{idx + 1}</span>
                        <Icon className="w-4 h-4 text-[#1F3A5F]" />
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-bold text-[#1F3A5F]">{poi.title}</h4>
                        <p className="text-sm text-[#4A5568] mt-1 leading-relaxed">{poi.description}</p>
                        {poi.tip && <p className="mt-1 text-xs text-[#E5276A] italic">💡 {poi.tip}</p>}
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}

          {mapArtifacts.filter((a) => a.type === "parcel").map((a) => (
            <section key={a.id} data-testid={`producer-parcels-${a.id}`}>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#E5276A] font-bold mb-3 inline-flex items-center gap-1.5"><Trees className="w-3.5 h-3.5" /> {a.name}</div>
              <div className="grid sm:grid-cols-2 gap-3">
                {(a.payload?.parcels || []).map((par, idx) => (
                  <div key={idx} className="rounded-xl border border-[#1F3A5F]/10 bg-white p-3 flex gap-3">
                    <span className="w-3 h-12 rounded-md shrink-0" style={{ background: par.color || "#1F3A5F" }} />
                    <div>
                      <h4 className="font-bold text-[#1F3A5F]">{par.name}</h4>
                      <p className="text-xs text-[#8B95A1]">{par.lieu_dit} · {par.area_ha} ha · {par.cepage}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {showClaim && <ClaimModal producer={item} onClose={() => setShowClaim(false)} />}

      <AtlasBlocksSection entityType="producer" slug={item.slug || slug} />
    </div>
  );
}
